import type { ItemId } from './types';

/**
 * Everything the trader sells and everything monsters can drop.
 *
 * Potions restore a *percentage* of your maximum pool rather than a flat
 * amount, so a single potion stays relevant from level 1 to level 50 and the
 * shop never needs tiered stock.
 */
export interface ItemDef {
  id: ItemId;
  name: string;
  /** Short label for the quick bar. */
  short: string;
  description: string;
  price: number;
  /** Fraction of maximum health restored. */
  healPercent?: number;
  /** Fraction of maximum mana restored. */
  manaPercent?: number;
  /** Sends the user back to the safe zone. */
  teleport?: boolean;
  color: string;
  /** How it is drawn on the ground and in the quick bar. */
  shape: 'flask' | 'scroll';
}

export const ITEMS: Record<ItemId, ItemDef> = {
  health: {
    id: 'health',
    name: 'Health Potion',
    short: 'HP',
    description: 'Restores 40% of your maximum health.',
    price: 30,
    healPercent: 0.4,
    color: '#e0544c',
    shape: 'flask',
  },
  mana: {
    id: 'mana',
    name: 'Mana Potion',
    short: 'MP',
    description: 'Restores 40% of your maximum mana.',
    price: 30,
    manaPercent: 0.4,
    color: '#4b86e0',
    shape: 'flask',
  },
  scroll: {
    id: 'scroll',
    name: 'Town Scroll',
    short: 'TP',
    description: 'Teleports you back to the safe zone.',
    price: 25,
    teleport: true,
    color: '#d9c07a',
    shape: 'scroll',
  },
};

export const SHOP_ORDER: ItemId[] = ['health', 'mana', 'scroll'];

export const ITEM_IDS: ItemId[] = ['health', 'mana', 'scroll'];

export function isItemId(value: unknown): value is ItemId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ITEMS, value);
}

export function emptyBag(): Record<ItemId, number> {
  return { health: 0, mana: 0, scroll: 0 };
}

/**
 * Consumables get more expensive as you level, so they stay a real cost rather
 * than pocket change once the coin faucet dwarfs 30 coins. Both the shop and the
 * server price through here, so the label always matches what is charged.
 */
export function itemPrice(def: ItemDef, level: number): number {
  return Math.round(def.price * (1 + Math.max(0, level) * 0.08));
}
